import Image from 'next/image'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import CarbonFootprintForm, {
  CarbonFootpringFormValues,
} from './carbon-footprint-form/CarbonFootprintForm'
import CarbonFootprintDetails, {
  CarbonFootprintDetailsProps,
} from './CarbonFootprintDetails'
import { CarbonFootprintImpactUnit } from './CarbonFootprintImpactUnit'
import Icon from '../../atoms/icons/icon/Icon'
import { Button } from '../../../shadcn/components/ui/button'
import { Card, CardContent } from '../../../shadcn/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../shadcn/components/ui/dialog'
import { cn } from '../../../shadcn/utils'

type CarbonFootprintDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  computeCarbonFootprint: {
    isLoading: boolean
    onSubmit: (data: CarbonFootpringFormValues) => void
    defaultValues: Partial<CarbonFootpringFormValues>
  }
  carbonFootprintData: CarbonFootprintDetailsProps
  houseLocation?: google.maps.LatLngLiteral
  hideTrigger?: boolean
}

const CarbonFootprintDialog = ({
  isOpen,
  onOpenChange,
  computeCarbonFootprint,
  carbonFootprintData,
  houseLocation,
  hideTrigger = false,
}: CarbonFootprintDialogProps) => {
  const getCircleState = () => {
    if (computeCarbonFootprint.isLoading) {
      return 'loading'
    }
    if (carbonFootprintData.totalEmission) {
      return 'complete'
    }
    return 'default'
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            variant='outline'
            className='flex h-[48px] w-full items-center justify-start gap-1 border-0 bg-green-100 px-3 hover:bg-green-100'
          >
            <Icon name='leaf' size={20} className='text-green-500' />
            <FormattedMessage
              id='ui.CarbonFootprintDialog.triggerLabel'
              defaultMessage='<u>Pour estimer l’empreinte carbone de votre événement <strong>cliquez ici</strong></u>'
              values={{
                strong: (chunks) => (
                  <strong className='font-semibold'>{chunks}</strong>
                ),
                u: (chunks) => <u>{chunks}</u>,
              }}
            />
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className='sm:max-w-[425px] flex !h-[897px] h-[100vh] flex-col md:h-[90vh] md:!max-h-[90vh]'>
        <DialogHeader>
          <DialogTitle>
            <div className='mr-8 flex items-center justify-between'>
              <h1 className='flex items-center gap-2'>
                <Icon name='leaf' size={28} className='text-green-500' />
                <FormattedMessage
                  id='ui.CarbonFootprintDialog.title'
                  defaultMessage='Calculer mon empreinte carbone'
                />
              </h1>
              <CarbonFootpringLogos className='hidden md:flex' />
            </div>
          </DialogTitle>
          <DialogClose className='absolute right-5 top-5 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring disabled:pointer-events-none data-[state=open]:text-foreground md:right-6 md:top-6'>
            <Icon name='cross-without-circle' className='size-4' />
          </DialogClose>
        </DialogHeader>
        <div className='flex flex-col gap-6 overflow-y-auto'>
          <CarbonFootpringLogos className='md:hidden' />
          <div className='mt-3 text-left text-sm text-grey-700'>
            <FormattedMessage
              id='ui.CarbonFootprintDialog.description'
              defaultMessage='Naboo s’engage pour l’écologie et vous permet de calculer l’empreinte carbone de votre évènement en toute simplicité. Grâce à une méthode de calcul conforme aux chiffres délivrés par l’ADEME, obtenez en moins d’une minute vos émissions.'
            />
          </div>
          <Card className='p-10'>
            <CardContent className='flex flex-col items-center gap-10 p-0 md:flex-row'>
              <CarbonFootprintLoader
                emission={carbonFootprintData.totalEmission}
                circleState={getCircleState()}
              />
              <div className='w-full flex-grow md:w-auto'>
                <CarbonFootprintForm
                  defaultValues={computeCarbonFootprint.defaultValues}
                  onSubmit={computeCarbonFootprint.onSubmit}
                  houseLocation={houseLocation}
                />
              </div>
            </CardContent>
          </Card>
          <CarbonFootprintDetails {...carbonFootprintData} />
        </div>
        <DialogFooter className='md:hidden'>
          <Button
            type='submit'
            form='carbon-footprint-form'
            className='flex items-center gap-1 border-0 bg-green-800'
          >
            <Icon name='leaf' size={16} className='text-green-500' />
            <span className='text-white'>
              <FormattedMessage
                id='ui.CarbonFootprintForm.buttonLabel'
                defaultMessage='Calculer mon empreinte'
              />
            </span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CarbonFootprintDialog

const CarbonFootpringLogos = ({ className }: { className?: string }) => {
  return (
    <div className={cn('flex gap-2', className)}>
      <Image
        width={49}
        height={24}
        src='/images/logo-ademe.svg'
        alt='Logo Ademe'
      />
      <Image
        width={86}
        height={24}
        src='/images/logo-greenhouse.svg'
        alt='Logo Greenhouse Gas Protocol'
      />
      <Image
        width={90}
        height={24}
        src='/images/logo-bilan-carbone.svg'
        alt='Logo Bilan Carbone'
      />
    </div>
  )
}

const CarbonFootprintLoader = ({
  emission,
  circleState,
}: {
  emission: number
  circleState: 'default' | 'loading' | 'complete'
}) => {
  return (
    <div className='relative flex h-[240px] w-[240px] items-center justify-center'>
      <div
        className={cn('absolute z-0 h-full w-full rounded-full', {
          ['bg-grey-300']: circleState === 'default',
          [s.circleLoading]: circleState === 'loading',
          [s.circleComplete]: circleState === 'complete',
        })}
      ></div>
      <div className='z-10 h-[225px] w-[225px] rounded-full bg-white'>
        {circleState !== 'loading' && (
          <div className='flex h-full w-full flex-col items-center justify-center gap-1'>
            <Icon name='leaf' size={24} className='text-green-500' />
            <div>
              <FormattedMessage
                id='ui.CarbonFootprintLoader.label'
                defaultMessage='Empreinte Carbone'
              />
            </div>
            <div
              className={cn('text-[40px] text-grey-500', {
                'text-green-800': circleState === 'complete',
              })}
            >
              {emission}
            </div>
            <CarbonFootprintImpactUnit />
          </div>
        )}
      </div>
    </div>
  )
}
